
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { extractFormSampleFromCsv, NEW_SAMPLES_CSV_VERSION } from '@/modules/analysis/helpers/extractFormSampleFromCsv';
import Dialog, { DialogData } from '@/components/dialog/Dialog.vue';
import SampleIdentificationInput from './inputs/SampleIdentificationInput.vue';
import { ErrorEnum } from '@/models/errorEnum';
import { NatureEnum } from '@/modules/analysis/models/natureEnum';

interface Sample {
  reference: string;
  origin: string;
  size: string;
  sizeUnit: string;
  samplingDate: string;
}

export interface FormSample {
  nature: null | string;
  count: null | number;
  samples: Sample[];
}

export const MAX_SAMPLES_SIZE = '50';

@Component({
  components: { SampleIdentificationInput, Dialog }
})
export default class BatchIdentificationCarousel extends Vue {
  protected readonly EXPORT_FILENAME = 'new-samples.csv';
  protected readonly SAMPLES_CSV_VERSION = NEW_SAMPLES_CSV_VERSION;
  protected readonly BUTTON_TIMEOUT = 600;
  protected readonly NatureEnum = NatureEnum;
  protected readonly MAIN_NATURES_NUMBER = 3;
  @Prop()
  protected value!: FormSample;
  protected inputFiles!: HTMLInputElement;
  protected inputMoreNature: string | null = null;
  protected inputCount: string | null = null;
  protected formSamples: FormSample = {
    nature: null,
    count: null,
    samples: []
  };
  protected uploadedFile: File | null = null;
  protected dialogError: DialogData = {
    isOpen: false,
    title: '',
    content: ''
  };
  protected dragover = false;
  protected isFormValid = false;
  protected loadingConfirm = false;
  // stepper
  protected steps = [1, 2, 3, 4];

  protected mounted(): void {
    this.inputFiles = document.createElement('input');
    this.inputFiles.accept =
      'text/csv, .csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel';
    this.inputFiles.type = 'file';
    this.inputFiles.oninput = this.onInputFiles;
  }

  @Watch('value', { immediate: true })
  protected onValueChange(): void {
    if (this.$refs.sampleCount) {
      (this.$refs.sampleCount as HTMLFormElement).resetValidation();
    }
    if (this.value.nature === null) {
      this.isFormValid = false;
    }
    this.inputCount = this.value.count?.toString() || null;
    this.formSamples = { ...this.value };
  }

  /**
   * The user has defined the sample nature. Resets all samples sizeUnit.
   */
  protected submitSampleNature(nature: string): void {
    this.formSamples.nature = nature;
    this.formSamples.samples.map((sample) => (sample.sizeUnit = ''));
  }

  /**
   * The user has defined the sample count. Generates the required sample identification line count.
   */
  protected submitSampleCount(): void {
    if (!!this.inputCount && (this.$refs.sampleCount as HTMLFormElement).validate()) {
      this.formSamples.count = parseInt(this.inputCount);
      if (this.formSamples.count === this.formSamples.samples.length) {
        return;
      }
      this.formSamples.samples = Array(this.formSamples.count)
        .fill(0)
        .map(() => {
          return {
            reference: '',
            origin: '',
            size: '',
            sizeUnit: '',
            samplingDate: new Date().toISOString().slice(0, 10),
            dateMenu: false
          };
        });
    }
  }

  /**
   * The user has gone through the last step. Form is valid, emit event to parent.
   */
  protected submitSampleIdentification(): void {
    if ((this.$refs.sampleIdentification as HTMLFormElement).validate()) {
      this.isFormValid = true;
      this.$emit('valid');
      this.$emit('submit', this.formSamples);
    }
  }

  /**
   * the user has selected a nature from the v-select input other than the main ones.
   */
  protected submitMoreNature(): void {
    if (this.inputMoreNature) {
      this.submitSampleNature(this.inputMoreNature);
      (this.$refs.formInputMoreNature as HTMLFormElement).resetValidation();
      this.inputMoreNature = null;
    }
  }

  /**
   * The user wish to modify the previously validated form: invalidate it and emit event to parent.
   */
  protected invalidateForm(): void {
    this.isFormValid = false;
    this.$emit('invalid');
  }

  /**
   * The user has input files after clicked into the upload button. Reset inputFiles value.
   */
  protected onInputFiles(): void {
    this.saveFile(this.inputFiles.files);
    this.inputFiles.value = '';
  }

  /**
   * The user has dropped files into the drag and drop zone.
   */
  protected onDropFiles(e: DragEvent): void {
    this.dragover = false;
    if (e?.dataTransfer?.files) {
      if (e.dataTransfer.files.length > 1) {
        this.openAndSetDialogErrorContent(ErrorEnum.INVALID_FILE_NUMBER);
      } else {
        this.saveFile(e.dataTransfer.files);
      }
    }
  }

  /**
   * The user has input or dropped files. Check and save csv file.
   */
  protected saveFile(files: FileList | null): void {
    if (files) {
      const file = files.item(0) as File;
      if (
        !file.type.startsWith('text/csv') &&
        !file.type.startsWith('application/vnd.ms-excel') && // Excel Files 97-2003
        !file.type.startsWith('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') // Excel Files 2007+
      ) {
        this.openAndSetDialogErrorContent(ErrorEnum.INVALID_FILE_EXTENSION);
        return;
      }
      this.uploadedFile = file;
    }
  }

  /**
   * Extract FormSample from csv, verify and emit event to parent.
   */
  protected addSamples(): void {
    this.loadingConfirm = true;
    setTimeout(
      () =>
        extractFormSampleFromCsv(this.uploadedFile as File)
          .then((res) => {
            this.formSamples.nature = res.nature;
            this.formSamples.count = res.count;
            this.inputCount = res.count?.toString() as string;
            this.formSamples.samples = res.samples;
            this.uploadedFile = null;
          })
          .catch((error: Error) => {
            let errorMessage = ErrorEnum.INTERNAL_SERVER_ERROR.toString();
            if (Object.keys(ErrorEnum).includes(error.message)) {
              errorMessage = error.message;
            }
            this.openAndSetDialogErrorContent(errorMessage);
            this.isFormValid = false;
            this.$emit('invalid');
          })
          .finally(() => (this.loadingConfirm = false)),
      this.BUTTON_TIMEOUT
    );
  }

  protected openAndSetDialogErrorContent(code: string): void {
    this.dialogError.isOpen = true;
    if (code === ErrorEnum.INTERNAL_SERVER_ERROR) {
      this.dialogError.title = this.$i18n.t(`common.InternalError.fileError.${code}.title`).toString();
      this.dialogError.content = this.$i18n.t(`common.InternalError.fileError.${code}.content`).toString();
    } else {
      this.dialogError.title = this.$i18n.t(`analysis.BatchIdentificationCarousel.fileError.${code}.title`).toString();
      this.dialogError.content = this.$i18n
        .t(`analysis.BatchIdentificationCarousel.fileError.${code}.content`)
        .toString();
    }
  }

  protected isValidNumber(value: string): boolean {
    var reg = /^\d+$/;
    return reg.test(value) && parseInt(value) > 0 && parseInt(value) <= parseInt(MAX_SAMPLES_SIZE);
  }

  /**
   * 1: nature step
   * 2: count step
   * 3: identification step
   * 4: form complete step
   */
  protected get sampleIdentificationStep(): number {
    if (this.formSamples.nature === null) {
      return 1;
    } else if (this.formSamples.count === null) {
      return 2;
    } else if (this.isFormValid) {
      return 4;
    } else {
      return 3;
    }
  }

  protected get identificationTitle(): string {
    if (!this.formSamples.nature) {
      return '';
    }

    const natureKey = this.formSamples.nature + 'Label';
    const natureLabel = this.$i18n.t(`common.natures.${natureKey}`).toString();

    return this.$i18n
      .t(`analysis.BatchIdentificationCarousel.identificationTitle`, {
        count: this.formSamples.count,
        nature: natureLabel
      })
      .toString();
  }

  protected get mainNatureEnumArray(): typeof NatureEnum {
    const mainNatureEnumArray = NatureEnum.slice(0, this.MAIN_NATURES_NUMBER);
    return mainNatureEnumArray;
  }

  protected get moreNatureEnumArray(): { text: string; value: string; icon: string }[] {
    const moreNatureEnumArray = NatureEnum.slice(this.MAIN_NATURES_NUMBER, undefined).map((nature) => {
      return { text: this.$i18n.t(nature.label).toString(), value: nature.value, icon: nature.icon };
    });
    return moreNatureEnumArray;
  }
}
